<script setup lang="ts">
import { ref, computed, provide } from 'vue';
import { useDisplay } from 'vuetify';
import { useCustomizerStore } from '@/shared/stores/customizer';
import sidebarItems from './sidebarItem';

import NavGroup from './NavGroup/index.vue';
import NavItem from './NavItem/index.vue';
import NavCollapse from './NavCollapse/NavCollapse.vue';
import NavButton from './NavButton/index.vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import SidebarProfile from '../vertical-header/SidebarProfile.vue';
import {
    checkMenuViewPermission,
    hasAnyMyLettagaMenuConfigured,
    resolveMenuDisplayName,
} from '@/shared/utils/permissionUtils';
import { isLocal, isDevelopment } from '@/shared/config/env';
import { Icon } from '@iconify/vue';

// 대체 이미지 import
import defaultUserImage from '@/assets/images/profile/user-1.jpg';

const customizer = useCustomizerStore();
const sessionStore = useSessionStore();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const { width } = useDisplay();

// My Lettaga 좌측메뉴: 구독 무관 노출. 권한 ON + mylSaved(부서 PRV025~028 저장)일 때만 view 필터.
// 구독/feature 확인창은 페이지 ensureAccess(useMyLettagaAccess)가 담당.

// jurisdiction에 따라 Entity 메뉴 경로 동적 설정
const sidebarMenu = computed(() => {
    const customerInfo = sessionStore.getCustomerInfo();
    const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
    const isJurisdictionMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const shouldApplyDeptMenuVisibility = true;
    const isDevEnv = isLocal() || isDevelopment();

    // sidebarItems를 복사하고 조건에 따라 메뉴 수정
    // Current Register(/register/current) 는 사이드바에서 완전 제거
    // (남기면 getMenuKeyFromRoute → /register 로 DB 메뉴명이 'Register' 로 덮여 중복처럼 보임)
    const mapped = sidebarItems
        .filter((item) => {
            if (!item) return false;
            if (item.title === 'Current Register') return false;
            if (item.to === '/register/current') return false;
            if (item.icon === 'book-bookmark-linear') return false;
            return true;
        })
        .map(item => {
        if (item.title === 'Entity') {
            return {
                ...item,
                to: isJurisdictionMY ? '/entity_my' : '/entity'
            };
        }
        // 운영 Register: MY → Register Book, SG 등 → 기존 /register
        if (item.title === 'Register') {
            return {
                ...item,
                to: isJurisdictionMY ? '/register/my' : '/register',
            };
        }
        return item;
    });

    // 권한 payload의 메뉴명(L_MENU.MENU_NAME)을 사이드바 title에 오버레이
    // - 경로 기반으로 menu key를 찾고, DB 메뉴명이 있으면 표시명을 치환
    // - DB 값이 없으면 기존 title 유지
    const permissions = sessionStore.getPermissions as any;
    const applyDbMenuTitles = (items: any[]): any[] => {
        const list = Array.isArray(items) ? items : [];
        return list.map((item) => {
            if (!item || typeof item !== 'object') return item;
            const nextItem = { ...item };

            if (!nextItem.button && nextItem.to && nextItem.to !== '#') {
                const resolvedTitle = resolveMenuDisplayName({
                    permissions,
                    routePath: String(nextItem.to),
                    t,
                    titleKey: nextItem.titleKey || null,
                    fallback: String(nextItem.title || ''),
                });
                if (resolvedTitle) {
                    nextItem.title = resolvedTitle;
                    // title은 이미 DB/i18n 결과를 반영했으므로 중복 번역 방지
                    if ('titleKey' in nextItem) delete nextItem.titleKey;
                }
            }

            if (Array.isArray(nextItem.children) && nextItem.children.length > 0) {
                nextItem.children = applyDbMenuTitles(nextItem.children);
            }
            return nextItem;
        });
    };
    const titled = applyDbMenuTitles(mapped);

    if (!shouldApplyDeptMenuVisibility) return titled;

    // permissions.menus[*].view 기준으로 메뉴를 필터링
    const permissionsForFilter = sessionStore.getPermissions as any;
    const filterByView = (items: any[]): any[] => {
        return items
            .map(item => {
                // My Lettaga: 구독 무관. 권한 ON + mylSaved 일 때만 view 필터(미저장/구세션이면 전부 노출)
                if (item.title === 'My Lettaga') {
                    const children = Array.isArray(item.children) ? item.children : [];
                    if (
                        permissionsForFilter?.isAuthEnabled
                        && hasAnyMyLettagaMenuConfigured(permissionsForFilter)
                    ) {
                        const parentMenu = permissionsForFilter?.menus?.['/mylettaga'];
                        // 부모 L_MENU에 ROUTE_PATH가 있으면 PRV025 view 필수; 없으면 자식만으로 판정
                        if (parentMenu && parentMenu.view !== true) return null;
                        const filteredChildren = filterByView(children);
                        if (filteredChildren.length === 0) return null;
                        return { ...item, children: filteredChildren };
                    }
                    return { ...item, children };
                }
                // Start Task 같은 버튼 항목도 buttonAction 경로가 있으면 view 필터를 적용
                if (item.button) {
                    if (typeof item.buttonAction === 'string') {
                        // Account Settings 버튼은 항상 노출
                        if (item.title === 'Account Settings') {
                            return item;
                        }
                        const canView = checkMenuViewPermission(permissionsForFilter, item.buttonAction);
                        return canView ? item : null;
                    }
                    return item;
                }

                // children가 있으면 children부터 필터링
                if (item.children && Array.isArray(item.children)) {
                    const filteredChildren = filterByView(item.children);
                    if (filteredChildren.length === 0) return null;
                    return { ...item, children: filteredChildren };
                }

                // to가 없거나 그룹 키('#')면 그대로 유지
                if (!item.to || item.to === '#') return item;

                // T-Sign 임시 메뉴: local/development 에서만 노출 + 권한(view) 적용
                if (item.to === '/TSignTemp') {
                    if (!isDevEnv) return null;
                    const canViewTSignTemp = checkMenuViewPermission(permissionsForFilter, item.to);
                    return canViewTSignTemp ? item : null;
                }

                // Account Settings는 항상 노출
                if (item.title === 'Account Settings') {
                    return item;
                }
                // Keep config in code but hide from sidebar UI.
                // Use stable identity (route + icon), because title can be overridden by DB menuName.
                if (item.to === '/tasks/tsign-history' && item.icon === 'signature-linear') {
                    return null;
                }

                const canView = checkMenuViewPermission(permissionsForFilter, item.to);
                if (item.title === 'T-Sign' || item.title === 'T-Sign History' || String(item.to || '').includes('tsign')) {
                    const routePath = String(item.to || '');
                    let mappedMenuKey: string | null = null;
                    if (routePath.startsWith('/tasks/tsign-history')) {
                        mappedMenuKey = '/tasks/tsign';
                    } else if (routePath.startsWith('/tasks/')) {
                        const segments = routePath.split('/').filter(Boolean);
                        mappedMenuKey = segments.length >= 2 ? `/${segments[0]}/${segments[1]}` : '/tasks';
                    } else {
                        const segments = routePath.split('/').filter(Boolean);
                        mappedMenuKey = segments.length ? `/${segments[0]}` : null;
                    }

                    const menuPermission = mappedMenuKey ? permissionsForFilter?.menus?.[mappedMenuKey] : null;
                    console.log('[Sidebar][TSign Debug]', {
                        title: item.title,
                        routePath,
                        mappedMenuKey,
                        isAuthEnabled: permissionsForFilter?.isAuthEnabled,
                        menuPermission,
                        view: menuPermission?.view,
                        canView
                    });
                }
                return canView ? item : null;
            })
            .filter(Boolean);
    };

    return filterByView(titled);
});

// mini-sidebar 스타일이 적용되는 최소 너비 (scss의 @media min-width: 1170px와 일치)
const MINI_SIDEBAR_BREAKPOINT = 1170;

// 화면 크기가 작을 때는 rail 모드 비활성화 (반쯤 열린 상태 방지)
const shouldUseRail = computed(() => {
    return customizer.mini_sidebar && width.value >= MINI_SIDEBAR_BREAKPOINT;
});

// 사이드바 hover 확장 상태 추적
const isRailExpanded = ref(false);

// 사이드바가 좁은(rail) 상태이고 hover 확장도 아님 → 가이드 버튼을 아이콘(?)만 표시.
const guideCollapsed = computed(() => shouldUseRail.value && !isRailExpanded.value);

// 자식 컴포넌트에서 사용할 수 있도록 provide
provide('sidebarRailExpanded', isRailExpanded);

// rail 상태 변경 핸들러 (hover로 인한 확장/축소)
const handleRailUpdate = (value: boolean) => {
    // value가 false면 확장됨 (hover 중), true면 축소됨
    isRailExpanded.value = !value;
};

// 세션에서 프로필 이미지 가져오기 (userImage 사용)
const userProfileImage = computed(() => {
    const userInfo = sessionStore.getUserInfo();
    const imageUrl = userInfo?.userImage;

    console.log('[VerticalSidebar] 프로필 이미지 computed 실행:', {
        hasUserInfo: !!userInfo,
        hasImageUrl: !!imageUrl,
        imageUrlType: typeof imageUrl,
        imageUrlLength: typeof imageUrl === 'string' ? imageUrl.length : 0,
        imageUrlPreview: typeof imageUrl === 'string' ? imageUrl.substring(0, 100) : imageUrl,
        userInfoKeys: userInfo ? Object.keys(userInfo) : []
    });

    // 세션에 이미지가 있는 경우
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.length > 0) {
        // Data URI, 절대 URL, 상대 경로 모두 처리
        if (imageUrl.startsWith('data:')) {
            // Data URI (이미지 바이너리)
            // Data URI 형식 검증: data:[<mediatype>][;base64],<data>
            const dataUriMatch = imageUrl.match(/^data:([^;]+)?(;base64)?,(.+)$/);
            if (dataUriMatch && dataUriMatch[3] && dataUriMatch[3].length > 0) {
                console.log('[VerticalSidebar] Data URI 형식 감지 (유효함)');
                return imageUrl;
            } else {
                console.warn('[VerticalSidebar] Data URI 형식이지만 유효하지 않음:', imageUrl.substring(0, 50));
                return defaultUserImage;
            }
        } else if (imageUrl.startsWith('/uploads/')) {
            // 상대 경로인 경우 절대 경로로 변환
            const fullUrl = `${window.location.origin}${imageUrl}`;
            console.log('[VerticalSidebar] 상대 경로를 절대 경로로 변환:', fullUrl);
            return fullUrl;
        } else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // 이미 절대 URL인 경우
            console.log('[VerticalSidebar] 절대 URL 형식 감지:', imageUrl);
            return imageUrl;
        } else {
            // 기타 형식 (예: 상대 경로지만 /uploads/로 시작하지 않는 경우)
            console.warn('[VerticalSidebar] 알 수 없는 이미지 URL 형식:', imageUrl);
            // 기본 이미지 반환
            return defaultUserImage;
        }
    }
    // 기본 이미지 (세션에 이미지가 없거나 비정상인 경우)
    console.log('[VerticalSidebar] 세션에 이미지 없음, 기본 이미지 사용');
    return defaultUserImage;
});

// 사용자 가이드 — 별도 윈도우 팝업으로 가이드 HTML 표시.
const openGuide = () => {
    const url = `${window.location.origin}/guide/lettaga-user-guide.html`;
    const w = 1100;
    const h = 800;
    // 화면 중앙 배치
    const left = Math.max(0, Math.round((window.screen.width - w) / 2));
    const top = Math.max(0, Math.round((window.screen.height - h) / 2));
    const features = `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    window.open(url, 'lettagaUserGuide', features);
};

// 이미지 로드 실패 시 기본 이미지로 대체
const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement;
    // 기본 이미지가 아닌 경우에만 변경 (무한 루프 방지)
    if (img.src !== defaultUserImage) {
        const userInfo = sessionStore.getUserInfo();
        const imageUrl = userInfo?.userImage;
        console.warn('[VerticalSidebar] 프로필 이미지 로드 실패, 기본 이미지로 대체', {
            failedSrc: img.src,
            sessionImageUrl: imageUrl,
            sessionImageUrlType: typeof imageUrl,
            sessionImageUrlLength: typeof imageUrl === 'string' ? imageUrl.length : 0,
            sessionImageUrlPreview: typeof imageUrl === 'string' ? imageUrl.substring(0, 100) : imageUrl,
            userInfoKeys: userInfo ? Object.keys(userInfo) : []
        });
        img.src = defaultUserImage;
    }
};
</script>

<template>
    <v-navigation-drawer
        left
        v-model="customizer.Sidebar_drawer"
        elevation="0"
        rail-width="75"
        app
        class="leftSidebar"
        :rail="shouldUseRail"
        expand-on-hover
        width="256"
        @update:rail="handleRailUpdate"
    >
        <!-- ---------------------------------------------- -->
        <!---Navigation -->
        <!-- ---------------------------------------------- -->
        <perfect-scrollbar class="scrollnavbar">
            <div class="profile">
                <div class="profile-img py-10 px-3" >
                    <div class="avatar-wrapper">
                        <img 
                            width="50"
                            height="50"
                            :src="userProfileImage"
                            :alt="t('menu.userProfile') || 'User Profile'"
                            @error="handleImageError"
                            class="avatar-image"
                        />
                    </div>
                </div>
                <div class="profile-name d-flex align-center px-3">
                    <div class="profile-logout w-100">
                        <SidebarProfile />
                    </div>
                </div>
            </div>
            <v-list class="py-3 px-4" style="overflow: visible;">
                <!---Menu Loop -->
                <template v-for="(item, i) in sidebarMenu" :key="i">
                    <!---Button Item -->
                    <NavButton :item="item" v-if="item.button" />
                    <!---Item Sub Header -->
                    <NavGroup :item="item" v-else-if="item.header" />
                    <!---If Has Child -->
                    <NavCollapse class="leftPadding" :item="item" :level="0" v-else-if="item.children" />
                    <!---Single Item-->
                    <NavItem :item="item" v-else class="leftPadding" />
                    <!---End Single Item-->
                </template>
            </v-list>
        </perfect-scrollbar>

        <!-- 하단 고정: 약관/개인정보처리방침/DPA 링크. 메뉴 목록과 같은 스크롤 영역에 두면
             메뉴가 길 때 맨 아래로 밀려 숨겨지므로, #append로 드로어 하단에 항상 고정한다. -->
        <template #append>
            <div v-if="!guideCollapsed" class="legal-links px-4 py-3">
                <router-link to="/terms" target="_blank" class="legal-inline-link">{{ t('common.legalConsent.linkTerms') }}</router-link>
                <span class="legal-link-sep">·</span>
                <router-link to="/privacy" target="_blank" class="legal-inline-link">{{ t('common.legalConsent.linkPrivacy') }}</router-link>
                <span class="legal-link-sep">·</span>
                <router-link to="/dpa" target="_blank" class="legal-inline-link">DPA</router-link>
            </div>
        </template>

        <!-- ---------------------------------------------- -->
        <!--- 하단 고정: 사용자 가이드 (클릭 시 윈도우 팝업) -->
        <!-- Help 메뉴(상단 ? 아이콘)로 통합되어 숨김 처리 -->
        <!-- ---------------------------------------------- -->
        <!-- <template #append>
            <div class="guide-append d-flex align-center justify-center py-4">
                <v-tooltip location="top" :text="t('menu.userGuide') || 'User Guide'">
                    <template #activator="{ props: tipProps }">
                        <button
                            v-bind="tipProps"
                            type="button"
                            class="guide-fab"
                            :class="{ 'guide-fab--icon': guideCollapsed }"
                            :aria-label="t('menu.userGuide') || 'User Guide'"
                            @click="openGuide"
                        >
                            <Icon
                                :icon="guideCollapsed ? 'solar:question-circle-bold' : 'solar:book-bold'"
                                :height="guideCollapsed ? 24 : 18"
                                class="guide-fab-icon"
                            />
                            <span v-if="!guideCollapsed" class="guide-fab-label">Guide</span>
                        </button>
                    </template>
                </v-tooltip>
            </div>
        </template> -->
    </v-navigation-drawer>
</template>

<style scoped>
/* 프로필 이미지 스타일 */
.profile-img {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 75px;
}

.avatar-wrapper {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--v-theme-surface), 0.1);
}

.avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

/* 하단 사용자 가이드 — 알약형 "Guide" 버튼 + 살짝 움직이는 애니메이션 */
.guide-fab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 18px;
    border-radius: 999px;
    color: #fff;
    background: rgb(var(--v-theme-primary));
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.4);
    cursor: pointer;
    border: none;
    outline: none;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    /* 살짝 위아래로 떠다니는 모션 */
    animation: guide-float 2.4s ease-in-out infinite;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.guide-fab:hover {
    transform: scale(1.06);
    box-shadow: 0 6px 18px rgba(var(--v-theme-primary), 0.55);
    animation-play-state: paused;
}
.guide-fab:active {
    transform: scale(0.97);
}
/* 좁은(rail) 상태 — 원형 아이콘(?)만 */
.guide-fab--icon {
    width: 44px;
    height: 44px;
    padding: 0;
    gap: 0;
    border-radius: 50%;
    justify-content: center;
}
.guide-fab-icon {
    display: block;
}
.guide-fab-label {
    white-space: nowrap;
}

@keyframes guide-float {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}

/* 모션 최소화 선호 사용자는 애니메이션 끔 */
@media (prefers-reduced-motion: reduce) {
    .guide-fab {
        animation: none;
    }
}

/* 사이드바 하단 약관/개인정보처리방침 링크 (#append로 고정 — 메뉴 스크롤과 무관하게 항상 노출) */
.legal-links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    column-gap: 6px;
    row-gap: 2px;
    font-size: 11px;
    line-height: 1.4;
    text-align: center;
}
.legal-inline-link {
    color: rgba(var(--v-theme-on-surface), 0.75);
    text-decoration: none;
    white-space: nowrap;
    font-weight: 500;
}
.legal-inline-link:hover {
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
}
.legal-link-sep {
    color: rgba(var(--v-theme-on-surface), 0.4);
}
</style>
